import { useEffect, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { trackLogin } from '../services/auth-tracking.service';
import { getLogger } from '../utils/logger';

const logger = getLogger('useAuthenticationTracking');

/**
 * Time window (in seconds) for detecting new user signups.
 * Users with created_at within this window are considered new signups.
 * 10 minutes allows time for email verification and first login.
 */
const SIGNUP_DETECTION_WINDOW_SECONDS = 600; // 10 minutes

/**
 * Determines if a user is a new signup based on their created_at timestamp.
 *
 * @param createdAt - ISO 8601 timestamp from Auth0 custom claim
 * @param updatedAt - ISO 8601 timestamp from Auth0 (fallback)
 * @returns true if user was created within SIGNUP_DETECTION_WINDOW_SECONDS, false otherwise
 */
const isNewUserSignup = (createdAt?: string, updatedAt?: string): boolean => {
  // Prefer created_at if available (set via Auth0 Action)
  const timestampToCheck = createdAt || updatedAt;

  if (!timestampToCheck) {
    logger.warn('[AUTH TRACKING] No created_at or updated_at available for signup detection');
    return false;
  }

  try {
    // Parse timestamp and normalize to UTC
    const userCreationTime = new Date(timestampToCheck).getTime();
    const currentTime = Date.now();

    // Calculate time elapsed since user creation
    const secondsSinceCreation = (currentTime - userCreationTime) / 1000;

    // Handle edge case: future timestamps (clock skew)
    if (secondsSinceCreation < 0) {
      logger.warn('[AUTH TRACKING] User creation timestamp is in the future', {
        createdAt,
        updatedAt,
        secondsSinceCreation
      });
      return false;
    }

    // Consider as new user if created within the detection window
    return secondsSinceCreation <= SIGNUP_DETECTION_WINDOW_SECONDS;
  } catch (error) {
    logger.warn('Failed to parse user dates for signup detection', {
      createdAt,
      updatedAt,
      error
    });
    return false;
  }
};

/**
 * Custom hook for tracking user authentication events (login, logout, signup).
 *
 * Automatically detects new user signups vs. standard logins by checking the
 * user's created_at timestamp. Sends tracking events to the backend API.
 *
 * @returns Auth0 authentication state (isAuthenticated, isLoading, user)
 */
export const useAuthenticationTracking = () => {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
  const wasAuthenticated = useRef(false);
  const hasTrackedLogin = useRef(false);

  // Memoize signup detection to avoid re-computation on every render
  const isNewUser = useMemo(() => {
    if (!user) return false;
    return isNewUserSignup(user.created_at, user.updated_at);
  }, [user?.created_at, user?.updated_at]);

  useEffect(() => {
    const handleAuthenticationChange = async () => {
      // Skip if still loading
      if (isLoading) return;

      // User just logged in
      if (isAuthenticated && !wasAuthenticated.current && user && !hasTrackedLogin.current) {
        try {
          logger.debug('User logged in, tracking login event', { userId: user.sub });

          // Get access token
          const token = await getAccessTokenSilently();

          // Determine login method (signup vs. standard)
          const method = isNewUser ? 'signup' : 'standard';

          // Track the login
          await trackLogin(method, token);
          logger.debug(`${method} tracked successfully`, {
            userId: user.sub,
            isNewUser,
            createdAt: user.created_at,
            updatedAt: user.updated_at
          });

          hasTrackedLogin.current = true;
        } catch (error) {
          logger.error('Failed to track login', error);
        }
      }

      // User just logged out - reset tracking flag
      if (!isAuthenticated && wasAuthenticated.current) {
        logger.debug('User logged out, resetting tracking flag');
        hasTrackedLogin.current = false;
        wasAuthenticated.current = false;
      }

      // Update wasAuthenticated when we have BOTH auth AND user data
      // This prevents race condition where isAuthenticated=true but user=null
      if (isAuthenticated && user) {
        wasAuthenticated.current = true;
      }
    };

    handleAuthenticationChange();
  }, [isAuthenticated, isLoading, user, getAccessTokenSilently, isNewUser]);

  return {
    isAuthenticated,
    isLoading,
    user,
  };
};